import type { SiteInfo, RecordedStep, RecordingSession } from './types';

/**
 * Get API base URL (configurable for dev/prod)
 */
export function getApiBaseUrl(): string {
  // Check if running in dev mode (localhost)
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8000';
  }

  // Production
  return 'https://api.warpway.ai';
}

/**
 * Validate API key and fetch site info
 */
export async function validateApiKey(apiKey: string): Promise<SiteInfo> {
  const apiBaseUrl = getApiBaseUrl();

  // Simple format validation - Site API keys start with "warp_"
  if (!apiKey || !apiKey.startsWith('warp_')) {
    throw new Error('Invalid API key format. Keys should start with "warp_"');
  }

  // Validate the key by fetching site info
  const response = await fetch(`${apiBaseUrl}/v1/keys/validate`, {
    method: 'GET',
    headers: {
      'Authorization': `WarpwayKey ${apiKey}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Invalid API key');
    }
    throw new Error(`Failed to validate API key: ${response.statusText}`);
  }

  const data = await response.json();

  return {
    id: data.siteId || data.site_id || data.id,
    name: data.name || data.siteName || 'Unknown',
    plan: data.plan || 'free',
  };
}

/**
 * Create a new recording session
 */
export async function createRecording(
  apiKey: string,
  session: RecordingSession
): Promise<string> {
  const apiBaseUrl = getApiBaseUrl();
  const response = await fetch(`${apiBaseUrl}/v1/recordings`, {
    method: 'POST',
    headers: {
      'Authorization': `WarpwayKey ${apiKey}`,
      'Content-Type': 'application/json',
      'X-Warpway-Dev': '1',
    },
    body: JSON.stringify({
      current_url: session.currentUrl,
      page_signature: session.pageSignature || [],
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to create recording: ${response.statusText}`);
  }

  const data = await response.json();
  return data.recording_id;
}

/**
 * Append steps to an existing recording
 */
export async function appendRecordingSteps(
  apiKey: string,
  recordingId: string,
  steps: RecordedStep[]
): Promise<void> {
  const apiBaseUrl = getApiBaseUrl();
  const response = await fetch(`${apiBaseUrl}/v1/recordings/${recordingId}`, {
    method: 'POST',
    headers: {
      'Authorization': `WarpwayKey ${apiKey}`,
      'Content-Type': 'application/json',
      'X-Warpway-Dev': '1',
    },
    body: JSON.stringify({
      steps: steps.map((step) => ({
        id: step.id,
        type: step.type,
        timestamp: step.timestamp,
        selectors: step.selectors,
        value: step.value,
        url: step.url,
        routePattern: step.routePattern,
        metadata: step.metadata,
      })),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to append recording steps: ${response.statusText}`);
  }
}

/**
 * Finalize a recording and create an action
 */
export async function finalizeRecording(
  apiKey: string,
  recordingId: string,
  actionData: {
    name: string;
    description?: string;
    intents?: string[];
    inputs?: Array<{
      variableName: string;
      stepIdentifier: string;
      fieldType?: string;
      label?: string;
      required?: boolean;
    }>;
  }
): Promise<{ draftId: string; draft: any }> {
  const apiBaseUrl = getApiBaseUrl();
  const response = await fetch(`${apiBaseUrl}/v1/recordings/${recordingId}/finalize`, {
    method: 'POST',
    headers: {
      'Authorization': `WarpwayKey ${apiKey}`,
      'Content-Type': 'application/json',
      'X-Warpway-Dev': '1',
    },
    body: JSON.stringify(actionData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Failed to finalize recording: ${response.statusText}`);
  }

  const data = await response.json();
  return {
    draftId: data.draft_id,
    draft: data.draft,
  };
}

/**
 * Track that recorder was opened with valid API key
 */
export async function trackRecorderOpened(apiKey: string, siteId: string): Promise<void> {
  const apiBaseUrl = getApiBaseUrl();

  try {
    await fetch(`${apiBaseUrl}/v1/sites/${siteId}/recorder-opened`, {
      method: 'POST',
      headers: {
        'Authorization': `WarpwayKey ${apiKey}`,
        'Content-Type': 'application/json',
        'X-Warpway-Dev': '1',
      },
    });
  } catch (error) {
    // Silently fail - this is just tracking
    console.debug('[Warpway SDK] Failed to track recorder opened:', error);
  }
}
